import { useMemo, useState, type CSSProperties } from 'react';
import { Check } from 'lucide-react';
import { ClientDirectorBookingPanel } from '~/modules-rocco/booking';
import { EnquiryDownloadsStep } from '~/modules/enquiry/component/EnquiryDownloadsStep';
import type { EnquiryDownloadLink } from '~/modules/enquiry/types/public-enquiry-form';
import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import { GATE_CTA_CLASS } from '~/modules-rocco/user/component/auth-modal/gate-cta';
import type { CalendarAvailabilityData } from '~/modules-rocco/booking/api/get-calendar-availability.server';

const HEADING_CLASS =
    'flex items-center gap-2.5 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]';
const BODY_CLASS =
    'text-[1.1875rem] leading-snug font-medium tracking-[-0.02em] text-[#181818] whitespace-pre-wrap';
const MUTED_CLASS =
    'text-[1.0625rem] leading-snug tracking-[-0.02em] text-[#828282]';
const SECONDARY_ACTION_CLASS =
    'mt-4 w-full text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]';

/**
 * The downloads list takes the gate's tokens through the theme variables it
 * already reads (the embed theme), rather than a second copy of its markup.
 */
const GATE_DOWNLOADS_THEME = {
    '--ap-form-accent': '#181818',
    '--ap-form-on-accent': '#f3f3f3',
    '--ap-form-radius': '1rem',
    '--ap-form-field-bg': '#f6f6f6',
    '--ap-form-field-border': 'transparent',
    '--ap-form-text': '#181818',
} as CSSProperties;

/**
 * Stands in for the message box and the send button while the visitor's
 * answers match a routing rule that declines them, so nobody writes an enquiry
 * that was never going to be sent. Announced politely: the form changes under
 * the reader.
 */
export const EnquiryGateDeclineNotice = ({ message }: { message: string }) => (
    <div
        role="status"
        aria-live="polite"
        className="animate-in rounded-[1rem] bg-[#f6f6f6] px-4 py-4 duration-300 fade-in"
    >
        <p className="m-0 text-[1rem] leading-snug tracking-[-0.02em] whitespace-pre-wrap text-[#161616]">
            {message}
        </p>
        <p className="m-0 mt-2 text-[0.875rem] tracking-[-0.02em] text-[#828282]">
            Change your answers above if this does not sound like your project.
        </p>
    </div>
);

export interface EnquiryGateDeclinedProps {
    professionalTitle: string;
    /** The routing rule's message. */
    message: string;
    /** Back to the form with the answers kept. */
    onChangeAnswers: () => void;
    onClose: () => void;
}

/** The server declined the enquiry: its rule's message, in place of a confirmation. */
export const EnquiryGateDeclined = ({
    professionalTitle,
    message,
    onChangeAnswers,
    onClose,
}: EnquiryGateDeclinedProps) => (
    <div
        className="mx-auto my-auto flex w-full max-w-86 flex-col"
        role="status"
        aria-live="polite"
    >
        <h2 className={HEADING_CLASS}>A note from {professionalTitle}</h2>
        <p className={`mt-5 ${BODY_CLASS}`}>{message}</p>
        <p className={`mt-4 ${MUTED_CLASS}`}>Your enquiry has not been sent.</p>
        <GateStickyFooter className="mt-8">
            <button type="button" onClick={onClose} className={GATE_CTA_CLASS}>
                Close
            </button>
            <button
                type="button"
                onClick={onChangeAnswers}
                className={SECONDARY_ACTION_CLASS}
            >
                Change my answers
            </button>
        </GateStickyFooter>
    </div>
);

export interface EnquiryGateDownloadsProps {
    downloads: EnquiryDownloadLink[];
    /** Where the same links were emailed. */
    email: string | null;
    /** The form's thank-you copy; a stock line otherwise. */
    message: string | null;
    onClose: () => void;
}

/** A gated download form's confirmation: the files the submission earned. */
export const EnquiryGateDownloads = ({
    downloads,
    email,
    message,
    onClose,
}: EnquiryGateDownloadsProps) => (
    <div className="mx-auto flex w-full max-w-86 flex-1 flex-col">
        <h2 className={`mt-9 ${HEADING_CLASS}`}>
            {downloads.length === 1
                ? 'Your download is ready'
                : 'Your downloads are ready'}
            <Check className="size-6 shrink-0" strokeWidth={2} />
        </h2>
        <p className={`mt-3 ${BODY_CLASS}`}>
            {message ?? 'Thanks! Your enquiry has been sent.'}
        </p>
        <div className="mt-6" style={GATE_DOWNLOADS_THEME}>
            <EnquiryDownloadsStep compact downloads={downloads} email={email} />
        </div>
        <div className="flex-1" />
        <GateStickyFooter className="mt-8" desktopClassName="mb-2">
            <button type="button" onClick={onClose} className={GATE_CTA_CLASS}>
                Close
            </button>
        </GateStickyFooter>
    </div>
);

export interface EnquiryGateBookingStepProps {
    /** Booking link the enquiry was routed to. */
    sessionId: number;
    /** The enquiry just created, so the booking is linked to it. */
    enquiryId: number | null;
    clientName: string;
    clientEmail: string | null;
    /** Files the submission earned, offered beneath the calendar. */
    downloads: EnquiryDownloadLink[];
    onClose: () => void;
    /** Made-up times for the Business Manager preview; nothing is loaded or booked. */
    previewAvailability?: CalendarAvailabilityData;
}

/**
 * An enquiry that routed to a meeting: the details are sent, and the reader
 * picks a time without leaving the gate. Skipping keeps the enquiry.
 */
export const EnquiryGateBookingStep = ({
    sessionId,
    enquiryId,
    clientName,
    clientEmail,
    downloads,
    onClose,
    previewAvailability,
}: EnquiryGateBookingStepProps) => {
    const [booked, setBooked] = useState(false);
    const extraFormFields = useMemo(
        () => (enquiryId ? { enquiryId: String(enquiryId) } : undefined),
        [enquiryId],
    );
    const prefill = useMemo(
        () => ({ clientName, clientEmail: clientEmail ?? undefined }),
        [clientName, clientEmail],
    );

    return (
        <div
            className="mx-auto flex w-full max-w-93.75 flex-1 flex-col"
            aria-live="polite"
        >
            <h2 className={`mt-4 ${HEADING_CLASS}`}>
                {booked ? 'Meeting booked' : 'Details sent'}
                <Check className="size-6 shrink-0" strokeWidth={2} />
            </h2>
            <p className={`mt-3 ${MUTED_CLASS}`}>
                {booked
                    ? 'A confirmation is on its way to your email.'
                    : 'Now pick a time that suits you to book your meeting.'}
            </p>
            {/* Once booked the heading above confirms it; the panel's own success view
                would repeat it, laid out for a wider column than this one. */}
            {booked ? null : (
                <div className="mt-6 overflow-hidden rounded-[1rem] bg-[#f6f6f6]">
                    <ClientDirectorBookingPanel
                        sessionId={sessionId}
                        active
                        prefill={prefill}
                        extraFormFields={extraFormFields}
                        onBooked={() => setBooked(true)}
                        previewAvailability={previewAvailability}
                    />
                </div>
            )}
            {downloads.length > 0 ? (
                <div className="mt-6" style={GATE_DOWNLOADS_THEME}>
                    <EnquiryDownloadsStep
                        compact
                        downloads={downloads}
                        email={clientEmail}
                    />
                </div>
            ) : null}
            <GateStickyFooter className="mt-8" desktopClassName="mb-2">
                <button
                    type="button"
                    onClick={onClose}
                    className={GATE_CTA_CLASS}
                >
                    {booked ? 'Done' : 'Skip for now'}
                </button>
            </GateStickyFooter>
        </div>
    );
};
